// pages/AdminSignup.tsx
import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { useNavigate, Link } from 'react-router-dom';
import useAdmin from './hooks/useAdmin';
import { BASE_URL } from './hooks/useAdmin';
import PasswordStrength from '../shared/components/PasswordStrength';
import ErrorDisplay from '../shared/components/ErrorDisplay';

interface FormData {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export default function AdminSignup() {
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setError, error, refreshCsrf } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    refreshCsrf().then(csrfToken => {
      if (!csrfToken) setError('Failed to load CSRF token');
    }).catch(err => setError(err.message || 'Failed to load CSRF token'));
  }, [refreshCsrf, setError]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');
    try {
      const csrfToken = await refreshCsrf();
      if (!csrfToken) throw new Error('No CSRF token');
      const sanitizedData = {
        name: DOMPurify.sanitize(data.name || ""),
        email: DOMPurify.sanitize(data.email || ""),
        phone: DOMPurify.sanitize(data.phone || ""),
        password: data.password,
      };
      const res = await fetch(`${BASE_URL}/admin/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedData),
      });
      const result = await res.json();
      if (!res.ok) {
        if (res.status === 429) throw new Error('Too many attempts. Please wait 15 minutes.');
        if (res.status === 403) throw new Error('CSRF token invalid.');
        if (res.status === 400) throw new Error('Invalid input. Please check your details.');
        throw new Error(result.error || 'Signup failed');
      }
      alert('Signup successful! Check email.');
      navigate('/admin/login');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">Admin Signup</h1>

        {error && <ErrorDisplay error={error} onDismiss={() => setError('')} />}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" name="_csrf" />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
              Full Name
            </label>
            <input
              id="name"
              placeholder="Full Name"
              {...register('name', { 
                pattern: { value: /^[A-Za-z\s]{2,50}$/, message: 'Name: 2-50 letters only' }, 
                required: 'Name required' 
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="Email"
              {...register('email', { 
                pattern: { value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i, message: 'Invalid email' }, 
                required: 'Email required' 
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className="text-red-600 text-sm">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
              Phone
            </label>
            <input
              id="phone"
              placeholder="Phone (+1234567890)"
              {...register('phone', { 
                pattern: { value: /^\+?[0-9]{10,15}$/, message: 'Invalid phone' }, 
                required: 'Phone required' 
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.phone}
            />
            {errors.phone && <p className="text-red-600 text-sm">{errors.phone.message}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              {...register('password', {
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                required: 'Password required',
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-12 focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.password}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-600 transform -translate-y-1/2"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>

            <PasswordStrength password={watch('password') || ''} />

            {errors.password && <p className="text-red-600 text-sm">{errors.password.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition transform hover:scale-105 disabled:opacity-70"
          >
            {loading ? 'Creating...' : 'Sign Up'}
          </button>
        </form>

        <p className="mt-4 text-center text-gray-600 text-sm">
          Have an account? <Link to="/admin/login" className="text-purple-600 hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}